import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode, vModelText as _vModelText, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "flex w-full min-h-screen h-screen" }
const _hoisted_2 = { class: "px-0 h-full flex flex-auto flex-column w-full text-sm overflow-auto" }
const _hoisted_3 = ["onClick"]
const _hoisted_4 = { class: "block white-space-nowrap overflow-hidden text-overflow-ellipsis" }
const _hoisted_5 = { class: "flex-initial w-full flex flex-column" }
const _hoisted_6 = { class: "relative text-gray-300 bg-gray-800 p-2 inline-flex justify-content-center align-items-center gap-1 border-bottom-1 border-gray-700" }
const _hoisted_7 = ["max"]
const _hoisted_8 = { class: "text-xs" }
const _hoisted_9 = { class: "relative flex-auto bg-gray-900" }

import { onBeforeMount, onMounted, toRefs, ref, watch } from 'vue';
import { useDownload } from '@/composables';
import * as pdfjsLib from 'pdfjs-dist/webpack';
import { EventBus, PDFLinkService, PDFViewer, NullL10n, ProgressBar } from 'pdfjs-dist/web/pdf_viewer';
import type { PDFDocumentProxy } from 'pdfjs-dist/types/src/display/api';
import 'pdfjs-dist/web/pdf_viewer.css';

interface Props {
  src: string;
}

interface AttachmentItem {
  filename: string;
  content: Uint8Array;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'PDFViewer',
  props: {
    src: {}
  },
  setup(__props: any) {

const props = __props;
const { src } = toRefs(props);

let pdfDocument: PDFDocumentProxy | null = null;
let pdfViewer: PDFViewer | null = null;
let loadingBar: ProgressBar | null = null;

const container = ref<HTMLDivElement | null>(null);
const loadingBarContainer = ref<HTMLDivElement | null>(null);
const attachmentsSidebarIsOpen = ref<boolean>(true);
const attachments = ref<AttachmentItem[]>([]);
const totalPages = ref<number>(0);
const currentPage = ref<number>(1);

const eventBus = new EventBus();
const pdfLinkService = new PDFLinkService({ eventBus });

const clamp = (n: number) => Math.min(Math.max(1, n || 1), totalPages.value || 1);

const downloadAttachmentItem = (item: AttachmentItem) => {
  const fileName = item.filename.replace(/(\.\w+)+$/, '');
  useDownload(item.content, fileName, 'mp3');
};

const toggleAttachmentsSidebar = () => {
  attachmentsSidebarIsOpen.value = !attachmentsSidebarIsOpen.value;
};

const zoomIn = () => pdfViewer?.increaseScale();
const zoomOut = () => pdfViewer?.decreaseScale();

const goToNextPage = () => {
  if (!pdfViewer) return;
  pdfViewer.nextPage();
  currentPage.value = pdfViewer.currentPageNumber;
};

const goToPreviousPage = () => {
  if (!pdfViewer) return;
  pdfViewer.previousPage();
  currentPage.value = pdfViewer.currentPageNumber;
};

watch(currentPage, (newPageNumber) => {
  if (!pdfViewer) return;
  const page = clamp(newPageNumber);
  if (page !== pdfViewer.currentPageNumber) {
    pdfViewer.currentPageNumber = page;
    currentPage.value = page;
  }
});

const progress = (level: number) => {
  if (!loadingBar) return;
  const percent = Math.round((Number.isFinite(level) ? level : 0) * 100);
  if (percent > (loadingBar.percent ?? 0)) loadingBar.percent = percent;
  if (percent >= 100 && loadingBarContainer.value) {
    loadingBarContainer.value.style.display = 'none';
  }
};

const initPDFViewer = () => {
  if (!container.value) throw new Error('PDF container not ready');
  return new PDFViewer({
    container: container.value,
    eventBus,
    useOnlyCssZoom: true,
    textLayerMode: 0,
    linkService: pdfLinkService,
    maxCanvasPixels: 8000000,
    l10n: NullL10n,
  });
};

const pdfDocumentLoad = (url: string) => {
  const loadingTask = pdfjsLib.getDocument(url);

  loadingTask.onProgress = (progressData: { loaded: number; total: number }) => {
    progress(progressData.loaded / progressData.total);

    if (progressData.loaded === progressData.total) {
      loadingBarContainer.value.style.display = 'none';
    }
  };

  return loadingTask.promise;
};

const initDocument = async (viewer: PDFViewer | null, url: string) => {
  if (!viewer) throw new Error('PDF viewer not initialised');
  pdfDocument = await pdfDocumentLoad(url);
  viewer.setDocument(pdfDocument);
  pdfLinkService.setDocument(pdfDocument, null);
};

let onPagesInit: () => void;
let onPageChanging: (arg: { pageNumber: number }) => void;

onBeforeMount(() => {
  document.body.classList.add('max-w-full');
});

onMounted(async () => {
  if (loadingBarContainer.value) loadingBar = new ProgressBar(loadingBarContainer.value);
  pdfViewer = initPDFViewer();
  pdfLinkService.setViewer(pdfViewer);

  onPagesInit = () => {
    if (!pdfViewer) return;
    pdfViewer.currentScaleValue = 'page-height';
    currentPage.value = pdfViewer.currentPageNumber;
    totalPages.value = pdfViewer.pagesCount;

    if (typeof src.value === 'string' && src.value.startsWith('blob:')) {
      try {
        window.URL.revokeObjectURL(src.value);
      } catch {
        // Failed to revoke object URL
      }
    }
  };

  onPageChanging = ({ pageNumber }: { pageNumber: number }) => {
    currentPage.value = pageNumber;
  };

  eventBus.on('pagesinit', onPagesInit);
  eventBus.on('pagechanging', onPageChanging);

  try {
    await initDocument(pdfViewer, src.value);

    pdfDocument
      ?.getAttachments?.()
      .then((results) => {
        attachments.value = results ? (Object.values(results) as AttachmentItem[]) : [];
      })
      .catch(() => {
        // Error getting attachments
      });
  } catch (e) {
    console.error("PDF URL expired or invalid. The PDF.js library can't open this URL.", e);
    alert(
      'Sorry, you do not have permission to access this PDF or the URL is invalid. If you think this is a mistake please contact Support.',
    );
  }
});

watch(
  () => src.value,
  async (n) => {
    if (!n) return;
    try {
      await initDocument(pdfViewer, n);
      pdfDocument
        ?.getAttachments?.()
        .then((results) => {
          attachments.value = results ? (Object.values(results) as AttachmentItem[]) : [];
        })
        .catch((e) => {
          console.error('Error getting attachments', e);
        });
    } catch (e) {
      console.error('Error reloading PDF', e);
    }
  },
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (attachments.value.length > 0)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: _normalizeClass(["flex-column bg-gray-800 border-right-1 border-gray-700 w-15rem", { flex: attachmentsSidebarIsOpen.value, hidden: !attachmentsSidebarIsOpen.value }])
        }, [
          _cache[1] || (_cache[1] = _createElementVNode("h3", { class: "px-3 pt-3 mb-2 font-bold text-gray-400" }, "Attachments", -1)),
          _createElementVNode("ul", _hoisted_2, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(attachments.value, (attachment) => {
              return (_openBlock(), _createElementBlock("li", {
                key: attachment.filename
              }, [
                _createElementVNode("button", {
                  type: "button",
                  class: "btn-hover btn-square-hover cursor-pointer bg-transparent border-none flex w-full text-gray-300 px-3 py-1",
                  title: "Click to Download",
                  onClick: ($event: any) => (downloadAttachmentItem(attachment))
                }, [
                  _createElementVNode("span", _hoisted_4, _toDisplayString(attachment.filename), 1)
                ], 8, _hoisted_3)
              ]))
            }), 128))
          ])
        ], 2))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_5, [
      _createElementVNode("div", _hoisted_6, [
        (attachments.value.length > 0)
          ? (_openBlock(), _createElementBlock("button", {
              key: 0,
              title: "Show/Hide Attachments",
              type: "button",
              class: "btn-hover text-center bg-gray-800 border-1 border-gray-500 text-gray-300 absolute left-0 ml-2",
              onClick: toggleAttachmentsSidebar
            }, " Attachments "))
          : _createCommentVNode("", true),
        _createElementVNode("button", {
          title: "Previous Page",
          type: "button",
          class: "btn-hover btn-square px-0 text-center bg-gray-800 border-1 border-gray-500 text-gray-300",
          onClick: goToPreviousPage
        }, " ‹ "),
        _createElementVNode("button", {
          title: "Next Page",
          type: "button",
          class: "btn-hover btn-square px-0 text-center bg-gray-800 border-1 border-gray-500 text-gray-300",
          onClick: goToNextPage
        }, " › "),
        _withDirectives(_createElementVNode("input", {
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((currentPage).value = $event)),
          max: totalPages.value,
          title: "Page",
          type: "text",
          class: "bg-gray-800 border-1 border-gray-500 text-gray-300 w-2rem"
        }, null, 8, _hoisted_7), [
          [
            _vModelText,
            currentPage.value,
            void 0,
            {
              number: true,
              lazy: true
            }
          ]
        ]),
        _createElementVNode("span", _hoisted_8, "of " + _toDisplayString(totalPages.value), 1),
        _createElementVNode("button", {
          title: "Zoom Out",
          type: "button",
          class: "btn-hover btn-square px-0 text-center bg-gray-800 border-1 border-gray-500 text-gray-300",
          onClick: zoomOut
        }, " - "),
        _createElementVNode("button", {
          title: "Zoom In",
          type: "button",
          class: "btn-hover btn-square px-0 text-center bg-gray-800 border-1 border-gray-500 text-gray-300",
          onClick: zoomIn
        }, " + ")
      ]),
      _createElementVNode("div", {
        ref_key: "loadingBarContainer",
        ref: loadingBarContainer,
        class: "loading-bar relative border-bottom-1 border-gray-700"
      }, [...(_cache[2] || (_cache[2] = [
        _createElementVNode("div", { class: "progress absolute left-0 overflow-hidden w-full h-full" }, [
          _createElementVNode("div", { class: "glimmer absolute top-0 left-0 h-full w-5rem" })
        ], -1)
      ]))], 512),
      _createElementVNode("div", _hoisted_9, [
        _createElementVNode("div", {
          id: "viewerContainer",
          ref_key: "container",
          ref: container,
          class: "overflow-auto absolute w-full h-full"
        }, [...(_cache[3] || (_cache[3] = [
          _createElementVNode("div", {
            id: "viewer",
            class: "pdfViewer"
          }, null, -1)
        ]))], 512)
      ])
    ])
  ]))
}
}

})